// src/components/StytchLogin.js
import React, { useState, useEffect, useRef } from 'react';
import { useStytch, useStytchUser } from '@stytch/react';
import { Navigate } from 'react-router';
import './Login.css';
import logo from '../images/logo.png';
import { clearLoginBootstrapState, logAuthEvent } from '../utils/auth';
import { getActiveHermetiqAttribution, recordAcquisitionEvent } from '../utils/acquisition';
import { buildFreshDashboardSessionRedirectTarget, buildLoginOriginDashboardRedirectTarget, buildSafeSameOriginPathRedirectTarget } from '../utils/redirects';
import { STYTCH_SESSION_DURATION_MINUTES } from '../utils/stytchSession';
import SigningInScreen from './SigningInScreen';

const LOGIN_REDIRECT_STORAGE_KEY = 'hermetiq:loginRedirectUrl';
const LOGIN_REDIRECT_STORAGE_TTL_MS = 60 * 60 * 1000;

function getRedirectUrlParam() {
  return new URLSearchParams(window.location.search).get('redirect_url');
}

export function clearStoredLoginRedirectUrl() {
  try {
    window.localStorage.removeItem(LOGIN_REDIRECT_STORAGE_KEY);
  } catch (error) {
    console.warn('Unable to clear stored login redirect URL:', error);
  }
}

function getStoredLoginRedirectUrl() {
  try {
    const rawStoredRedirect = window.localStorage.getItem(LOGIN_REDIRECT_STORAGE_KEY);
    if (!rawStoredRedirect) {
      return null;
    }

    const storedRedirect = JSON.parse(rawStoredRedirect);
    if (
      !storedRedirect?.redirectUrl ||
      !storedRedirect?.createdAt ||
      Date.now() - Number(storedRedirect.createdAt) > LOGIN_REDIRECT_STORAGE_TTL_MS
    ) {
      clearStoredLoginRedirectUrl();
      return null;
    }

    return buildSafeSameOriginPathRedirectTarget(storedRedirect.redirectUrl, '/dashboard');
  } catch (error) {
    console.warn('Unable to read stored login redirect URL:', error);
    clearStoredLoginRedirectUrl();
    return null;
  }
}

export function rememberLoginRedirectUrl() {
  const redirectUrl = getRedirectUrlParam();
  if (!redirectUrl) {
    clearStoredLoginRedirectUrl();
    return;
  }

  const safeRedirectUrl = buildSafeSameOriginPathRedirectTarget(redirectUrl, '/dashboard');
  try {
    window.localStorage.setItem(
      LOGIN_REDIRECT_STORAGE_KEY,
      JSON.stringify({
        redirectUrl: safeRedirectUrl,
        createdAt: Date.now(),
      })
    );
  } catch (error) {
    console.warn('Unable to store login redirect URL:', error);
  }
}

export function getPostLoginRedirectUrl(loginOrigin = window.location.origin) {
  const fallbackPath = '/dashboard';
  const redirectUrl = getRedirectUrlParam();
  const path = redirectUrl
    ? buildSafeSameOriginPathRedirectTarget(redirectUrl, fallbackPath)
    : (getStoredLoginRedirectUrl() || fallbackPath);

  // /authorize must finish on the Stytch login origin. Other destinations
  // belong on the dashboard host so oauth2-proxy can establish its session.
  if (new URL(path, loginOrigin).pathname === '/authorize') return path;
  return buildLoginOriginDashboardRedirectTarget(path, loginOrigin) || path;
}

export function getLoginCallbackUrl() {
  const callbackUrl = new URL('/login', window.location.origin);
  return callbackUrl.toString();
}

// <Navigate> resolves `to` as an in-app path, so the absolute dashboard URL the
// login origin redirects to became /login/https:/dashboard.hermetiq.io/dashboard
// and rendered the dashboard on the login origin, where every API call fails CORS.
function CrossOriginRedirect({ url }) {
  useEffect(() => {
    window.location.replace(url);
  }, [url]);
  return null;
}

function reportAttributedAuthenticationEvent(event) {
  const attribution = getActiveHermetiqAttribution(window.location.search);
  if (attribution) void recordAcquisitionEvent(event, attribution);
}

function StytchLogin() {
  // A cached SDK user may appear before a new one-time callback is exchanged.
  // Claim the callback on the first render, before the redirect effect can run.
  const [callbackParams] = useState(() => new URLSearchParams(window.location.search));
  const [isAuthenticating, setIsAuthenticating] = useState(() =>
    !callbackParams.get('error') && !!(callbackParams.get('token') && callbackParams.get('stytch_token_type'))
  );
  const [authError, setAuthError] = useState(() => callbackParams.get('error')
    ? `Authentication failed: ${callbackParams.get('error')}` : null);

  const hasProcessedToken = useRef(false);
  const stytch = useStytch();
  const { user, isInitialized } = useStytchUser();

  // Handle OAuth/Magic Link returns
  useEffect(() => {
    if (hasProcessedToken.current) return;

    const token = callbackParams.get('token');
    const tokenType = callbackParams.get('stytch_token_type');
    const error = callbackParams.get('error');

    if (error) {
      hasProcessedToken.current = true;
      setAuthError(`Authentication failed: ${error}`);
      clearLoginBootstrapState();
      logAuthEvent('login:callback-error', { error });
      if (stytch && stytch.session) {
        try {
          stytch.session.revoke();
        } catch (error2) {
          // Session might not exist, which is fine
        }
      }
      const clearedParams = new URLSearchParams(window.location.search);
      clearedParams.delete('error');
      clearedParams.delete('token');
      clearedParams.delete('stytch_token_type');
      const nextSearch = clearedParams.toString();
      window.history.replaceState({}, document.title, `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`);
      return;
    }

    if (!token || !tokenType) return;

    hasProcessedToken.current = true;
    setIsAuthenticating(true);

    const clearedParams = new URLSearchParams(window.location.search);
    clearedParams.delete('token');
    clearedParams.delete('stytch_token_type');
    const nextSearch = clearedParams.toString();
    window.history.replaceState({}, document.title, `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`);

    const handleAuth = async () => {
      try {
        const postLoginRedirectUrl = getPostLoginRedirectUrl();
        clearLoginBootstrapState();
        logAuthEvent('login:authenticate-token', { tokenType });
        if (tokenType === 'oauth') {
          await stytch.oauth.authenticate(token, { session_duration_minutes: STYTCH_SESSION_DURATION_MINUTES });
        } else if (tokenType === 'magic_links') {
          await stytch.magicLinks.authenticate(token, { session_duration_minutes: STYTCH_SESSION_DURATION_MINUTES });
        }
        reportAttributedAuthenticationEvent('Authentication completed');
        logAuthEvent('login:authenticate-token-succeeded', {
          tokenType,
          redirectUrl: postLoginRedirectUrl,
        });
        clearStoredLoginRedirectUrl();
        // Sign out of the shared .hermetiq.io / .hermetiq.dev oauth2-proxy
        // session cookie first, so the dashboard signs in as this new identity.
        window.location.replace(buildFreshDashboardSessionRedirectTarget(postLoginRedirectUrl));
      } catch (err) {
        console.error('Authentication failed:', err);
        clearLoginBootstrapState();
        logAuthEvent('login:authenticate-token-failed', {
          tokenType,
          message: err?.message,
        });
        reportAttributedAuthenticationEvent('Authentication failed');
        if (stytch && stytch.session) {
          try {
            await stytch.session.revoke();
          } catch (ignore) {}
        }
        setAuthError('Authentication failed. Please try again.');
        setIsAuthenticating(false);
      }
    };

    handleAuth();
  }, [callbackParams, stytch]);

  if (user && isInitialized && !isAuthenticating && !authError) {
    const redirectUrl = getPostLoginRedirectUrl();
    clearStoredLoginRedirectUrl();
    logAuthEvent('login:redirect-authenticated-user', {
      redirectUrl,
      userId: user?.user_id || null,
    });
    if (/^https?:\/\//i.test(redirectUrl)) {
      return <CrossOriginRedirect url={redirectUrl} />;
    }
    return <Navigate to={redirectUrl} replace />;
  }

  const handleGitHubLogin = async () => {
    setAuthError(null);
    try {
      const callbackUrl = getLoginCallbackUrl();
      rememberLoginRedirectUrl();
      reportAttributedAuthenticationEvent('Authentication started');
      await stytch.oauth.github.start({
        login_redirect_url: callbackUrl,
        signup_redirect_url: callbackUrl,
      });
    } catch (err) {
      console.error('GitHub login failed:', err);
      reportAttributedAuthenticationEvent('Authentication failed');
      setAuthError('Failed to start GitHub login. Please try again.');
    }
  };

  const handleGoogleLogin = async () => {
    setAuthError(null);
    try {
      const callbackUrl = getLoginCallbackUrl();
      rememberLoginRedirectUrl();
      reportAttributedAuthenticationEvent('Authentication started');
      await stytch.oauth.google.start({
        login_redirect_url: callbackUrl,
        signup_redirect_url: callbackUrl,
      });
    } catch (err) {
      console.error('Google login failed:', err);
      reportAttributedAuthenticationEvent('Authentication failed');
      setAuthError('Failed to start Google login. Please try again.');
    }
  };


  if (isAuthenticating) {
    return <SigningInScreen />;
  }

  return (
    <div className="login-page">
      <div className="login-background"><div className="gradient-bg"></div><div className="grid-overlay"></div></div>
      <div className="login-container">
        <div className="login-box">
          <div className="login-brand">
            <img src={logo} alt="Hermetiq" className="login-logo" />
            <h1 className="login-title">Hermetiq</h1>
            <p className="login-tagline">Next-Generation Build Intelligence</p>
          </div>

          {authError && (
            <div style={{
              padding: '10px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '5px',
              marginBottom: '15px',
              color: '#c33'
            }}>
              {authError}
            </div>
          )}

          <div className="login-form">
            {/* Social login only. Email Magic Link is disabled behind the
                oauth2-proxy BFF (the SDK EML flow doesn't complete through the
                proxy); re-enable if EML support is added later. */}
            <div style={{display: 'flex', gap: '12px'}}>
              <button
                type="button"
                onClick={handleGoogleLogin}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#fff',
                  color: '#333',
                  border: '1px solid #ddd',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Google
              </button>

              <button
                type="button"
                onClick={handleGitHubLogin}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#333',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path
                    d="M12 .5a11.5 11.5 0 0 0-3.637 22.414c.575.106.785-.25.785-.556v-2.025c-3.193.695-3.867-1.362-3.867-1.362-.523-1.33-1.278-1.685-1.278-1.685-1.044-.713.08-.699.08-.699 1.154.081 1.761 1.186 1.761 1.186 1.027 1.761 2.695 1.252 3.35.958.104-.743.402-1.252.731-1.54-2.55-.291-5.232-1.274-5.232-5.668 0-1.252.447-2.276 1.184-3.078-.118-.29-.513-1.461.112-3.047 0 0 .966-.309 3.17 1.176a10.98 10.98 0 0 1 5.772 0c2.205-1.485 3.17-1.176 3.17-1.176.626 1.586.231 2.757.114 3.047.738.802 1.183 1.826 1.183 3.078 0 4.405-2.688 5.374-5.252 5.662.414.356.783 1.055.783 2.128v3.15c0 .309.209.668.792.555A11.5 11.5 0 0 0 12 .5z"/>
                </svg>
                GitHub
              </button>
            </div>
          </div>

          <div className="login-footer">
            <p>
              By continuing, you agree to our{' '}

              <a href="https://www.hermetiq.com/terms-of-service"
                 target="_blank"
                 rel="noopener noreferrer"
                 className="terms-link"
              >
                Terms of Service
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StytchLogin;
